import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { createHash } from "utils/helperFunctions";
import { RootState } from "types";

import CALL from "_DatabaseFunctions/cloudCalls";
import DB from "_DatabaseFunctions/directConnect";

import { Button } from "@mui/material";

import ModalTemplate from "app/Components/Modals/ModalTemplate";

interface CurPlayersModalProps {
  dets: {
    open: boolean;
    status: string;
    playerData: any;
  };
  gameInfo: any;
}

function CurPlayersModal({ dets, gameInfo }: CurPlayersModalProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const { playerData } = dets;
  const [open, setShow] = useState<boolean>(false);
  const [modalData, setModalData] = useState({});
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  useEffect(() => {
    setShow(dets.open);
    if (dets.status && dets.status.trim() !== "") {
      renderModalInfo(dets.status);
    }
  }, [dets]);

  const renderModalInfo = (status: string) => {
    switch (status) {
      case "confirmPlayer":
        setModalData({
          status,
          title: "Confirm Player's Seat",
          msg: `Confirm ${playerData.displayName}'s Seat Now?`,
          type: "info",
        });
        break;
      case "removePlayer":
        setModalData({
          status,
          title: "Remove Player",
          msg: `Remove ${playerData.displayName} from Game?`,
          type: "info",
        });
        break;
      case "errorRunning":
        setModalData({
          title: "Error Adding Player",
          msg: `Game Must be Running Before Players Can be Added to Table`,
          type: "error",
        });
        break;
      case "errorMaxPlayers":
        setModalData({
          title: "Max Players Error",
          msg: `Maximum Number of Players (${gameInfo.maxPlayers}) Already at Table`,
          type: "error",
        });
        break;
      default:
        console.warn(`CurPlayersModal: Unhandled status "${status}"`);
        setModalData({
          title: "Unknown Action",
          msg: "An unknown action was requested. Please try again.",
          type: "error",
        });
        break;
    }
  };

  const confirmAddPlayer = async () => {
    const playerId = playerData.id;
    const gameId = gameInfo.id;
    const reqId = createHash(playerId + gameId);
    DB.hostConfirmReq(reqId);
    DB.confirmAddPlayer(gameId, playerId);
    await DB.startPlayerSession({
      gameSessionId: gameInfo.sessionId,
      hostId: user.uid,
      gameName: gameInfo.gameName,
      gameId: gameInfo.id,
      playerId: playerData.id,
      playerName: playerData.displayName,
    });
    await CALL.sendConfirmationAcceptedEmail(playerId, gameInfo.gameName);
  };

  const removePlayerFromTable = async () => {
    const playerId = playerData.id;
    const gameId = gameInfo.id;

    setIsRemoving(true);
    
    try {
      const reqId = createHash(playerId + gameId);
      
      // End player session first (gameSessionId is optional as the function can look it up)
      await DB.endPlayerSession({
        gameSessionId: gameInfo.gameSessionId || null,
        gameId: gameId,
        playerId: playerId,
      });
      
      // Remove player from game
      const result = await DB.removePlayerFromGame(gameId, playerId);
      
      if (result === "success") {
        // Delete the request
        await DB.deleteRequest(reqId);
        console.log(`Successfully removed player ${playerData.displayName} from game`);
        
        // Close modal after successful removal
        setShow(false);
      } else {
        console.error("Failed to remove player:", result);
      }
    } catch (error) {
      console.error("Error removing player from table:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  const modalChildren = () => {
    if (
      modalData.status === "confirmPlayer" ||
      modalData.status === "removePlayer"
    ) {
      const ChildMap = {
        confirmPlayer: {
          callback: confirmAddPlayer,
          msg: `${playerData.displayName}'s Seat is Confirmed`,
        },
        removePlayer: {
          callback: removePlayerFromTable,
          msg: `${playerData.displayName} Removed Successfully!`,
        },
      };

      const { callback, msg } = ChildMap[modalData.status];
      return (
        <div>
          {modalData.type === "info" ? (
            <Button
              variant="contained"
              color="success"
              disabled={isRemoving}
              onClick={async () => {
                await callback();
                if (modalData.status !== "removePlayer") {
                  // Only show success message for confirm, not remove (since remove closes modal)
                  setModalData({
                    type: "success",
                    title: "Success",
                    msg,
                  });
                }
              }}
            >
              {isRemoving && modalData.status === "removePlayer" ? "Removing..." : "Confirm"}
            </Button>
          ) : (
            <></>
          )}
          <Button variant="contained" color="error" onClick={() => setShow(false)}>
            Cancel
          </Button>
        </div>
      );
    }
    return (
      <Button variant="contained" color="error" onClick={() => setShow(false)}>
        Close
      </Button>
    );
  };

  return (
    <ModalTemplate
      title={modalData.title}
      modalType={modalData.type}
      msg={modalData.msg}
      show={open}
      onClose={() => setShow(false)}
      action={modalData.action}
    >
      {modalChildren()}
    </ModalTemplate>
  );
}

export default CurPlayersModal;
